import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CalendarCheck, Video, Clock, AlertTriangle, ExternalLink, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const CalendarEvents = () => {
  const [events, setEvents] = useState([]);
  const [calendarEnabled, setCalendarEnabled] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/calendar/events`, { withCredentials: true });
      setEvents(data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/integrations/status`, { withCredentials: true });
      const cal = data.integrations?.find(i => i.service === 'google_calendar');
      setCalendarEnabled(cal?.enabled || false);
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchEvents(); fetchStatus(); }, [fetchEvents, fetchStatus]);

  return (
    <div className="space-y-6" data-testid="calendar-events">
      <div>
        <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Google Calendar
        </h2>
        <p className="text-[#5F6B85]">Eventos e links de videochamada</p>
      </div>

      {calendarEnabled ? (
        <Card className="border-[#0F1B3D] bg-[#0F1B3D]/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-[#0F1B3D]" />
              <div>
                <p className="text-sm font-medium text-[#0F1B3D]">Google Calendar Conectado</p>
                <p className="text-xs text-[#5F6B85]">
                  Eventos sincronizados com o calendário do Gmail da Dra. Fernanda.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E9C46A] bg-[#E9C46A]/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[#E9C46A]" />
              <div>
                <p className="text-sm font-medium text-[#0F1B3D]">Integração Simulada</p>
                <p className="text-xs text-[#5F6B85]">
                  Configure as credenciais Google Calendar API em Configurações para ativar.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {events.length === 0 ? (
          <Card className="border-[#E5DCC8]">
            <CardContent className="p-8 text-center">
              <CalendarCheck className="h-12 w-12 text-[#7A85A0] mx-auto mb-3" />
              <p className="text-[#7A85A0]">Nenhum evento no calendário</p>
              <p className="text-sm text-[#7A85A0] mt-1">
                Vá em Consultas e clique no ícone de calendário para criar um evento com link Meet
              </p>
            </CardContent>
          </Card>
        ) : (
          events.map((evt) => (
            <Card key={evt.event_id} className="border-[#E5DCC8]" data-testid={`calendar-evt-${evt.event_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-[#0F1B3D]/10 rounded-lg mt-1">
                      <CalendarCheck className="h-5 w-5 text-[#0F1B3D]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F1B3D] mb-1">{evt.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-[#5F6B85]">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(evt.date).toLocaleDateString('pt-BR')} às {evt.time}
                        </span>
                        <span>{evt.duration_minutes} min</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          evt.status === 'created' ? 'bg-[#0F1B3D]/10 text-[#0F1B3D]' :
                          evt.status === 'created_mock' ? 'bg-[#E9C46A]/10 text-[#E9C46A]' :
                          'bg-blue-50 text-blue-700'
                        }`}>
                          {evt.status === 'created' ? 'Sincronizado' : evt.status === 'created_mock' ? 'Local' : evt.status}
                        </span>
                      </div>
                      {evt.meet_link && (
                        <div className="flex items-center gap-2 mt-2">
                          <Video className="h-4 w-4 text-[#0F1B3D]" />
                          <a
                            href={evt.meet_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#0F1B3D] underline flex items-center gap-1"
                          >
                            {evt.meet_link} <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
